import { isUrl, prepareValidUrl } from "@/lib/utils";
import { useState } from "react";

interface UseUrlValidationResult {
  validateUrl: (url: string) => {
    isValid: boolean;
    validUrl?: string;
    errorMessage?: string;
  };
  showError: boolean;
  errorMessage: string;
  setShowError: (show: boolean) => void;
  setErrorMessage: (message: string) => void;
}

/**
 * Custom hook for URL validation and error handling
 * Used to centralize URL validation logic across components
 */
export function useUrlValidation(): UseUrlValidationResult {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Validates a URL and returns validation result
   * @param url - The URL to validate
   * @returns Object containing validation result
   */
  const validateUrl = (url: string) => {
    // Reset error state
    setShowError(false);

    // Check if URL is empty
    if (!url.trim()) {
      setErrorMessage("Please enter a URL");
      setShowError(true);
      return { isValid: false, errorMessage: "Please enter a URL" };
    }

    // Check if URL is valid
    if (!isUrl(url)) {
      const message =
        "Please enter a valid URL (e.g., example.com or https://example.com)";
      setErrorMessage(message);
      setShowError(true);
      return { isValid: false, errorMessage: message };
    }

    // Prepare valid URL with proper protocol
    const validUrl = prepareValidUrl(url);
    return { isValid: true, validUrl };
  };

  return {
    validateUrl,
    showError,
    errorMessage,
    setShowError,
    setErrorMessage,
  };
}
